import { formatFileSize } from '../crypto/sharedFolderFile';
import { applyI18n, t } from '../i18n';
import { bindSimpleFileDropzone } from './filesDropUpload';

export interface InfoShareAttachmentField {
  getFiles(): File[];
  clear(): void;
  dispose(): void;
}

export function mountInfoShareAttachmentField(
  container: HTMLElement,
  options?: {
    labelKey?: string;
    hintKey?: string;
    inputName?: string;
    isBusy?: () => boolean;
  },
): InfoShareAttachmentField {
  const labelKey = options?.labelKey ?? 'info.owner.attachments';
  const hintKey = options?.hintKey ?? 'info.owner.attachmentsHint';
  const inputName = options?.inputName ?? 'attachments';

  const field = document.createElement('div');
  field.className = 'info-share-files';
  field.innerHTML = `
    <div class="info-share-files__header">
      <span data-i18n="${labelKey}"></span>
    </div>
    <div class="info-share-files__controls">
      <label class="button ghost info-share-files__browse">
        <span data-i18n="info.owner.attachmentsBrowse"></span>
        <input type="file" name="${inputName}" multiple hidden />
      </label>
      <span class="muted" data-i18n="${hintKey}"></span>
    </div>
    <p class="error-text info-share-files__drop-error" hidden></p>
    <ul class="info-share-files__list" hidden></ul>
  `;
  container.append(field);
  applyI18n(field);

  const input = field.querySelector('input[type="file"]') as HTMLInputElement;
  const list = field.querySelector('.info-share-files__list') as HTMLUListElement;
  const dropError = field.querySelector('.info-share-files__drop-error') as HTMLParagraphElement;
  let selected: File[] = [];
  let busy = false;

  function clearDropError(): void {
    dropError.hidden = true;
    dropError.textContent = '';
  }

  function showDropError(): void {
    dropError.hidden = false;
    dropError.textContent = t('files.drop.emptyRejected');
  }

  function syncInputFiles(): void {
    try {
      const transfer = new DataTransfer();
      for (const file of selected) {
        transfer.items.add(file);
      }
      input.files = transfer.files;
    } catch {
      // jsdom and some browsers may not support assigning FileList via DataTransfer.
    }
  }

  function renderList(): void {
    list.replaceChildren();
    if (selected.length === 0) {
      list.hidden = true;
      return;
    }

    list.hidden = false;
    selected.forEach((file, index) => {
      const item = document.createElement('li');
      item.className = 'info-share-files__item';
      const meta = document.createElement('div');
      meta.className = 'info-share-files__meta';
      const name = document.createElement('span');
      name.className = 'info-share-files__name';
      name.textContent = file.name;
      const size = document.createElement('span');
      size.className = 'info-share-files__size muted';
      size.textContent = formatFileSize(file.size);
      meta.append(name, size);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'button ghost info-share-files__remove';
      remove.textContent = t('info.owner.attachmentsRemove');
      remove.addEventListener('click', () => {
        selected = selected.filter((_, i) => i !== index);
        syncInputFiles();
        renderList();
      });
      item.append(meta, remove);
      list.append(item);
    });
  }

  function addFiles(files: File[]): void {
    if (files.length === 0) {
      return;
    }
    clearDropError();
    const existing = new Set(selected.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
    for (const file of files) {
      const key = `${file.name}:${file.size}:${file.lastModified}`;
      if (existing.has(key)) {
        continue;
      }
      existing.add(key);
      selected.push(file);
    }
    syncInputFiles();
    renderList();
  }

  input.addEventListener('change', () => {
    addFiles([...input.files ?? []]);
  });

  const unbindDrop = bindSimpleFileDropzone({
    root: field,
    canAccept: () => !busy,
    isBusy: () => busy || Boolean(options?.isBusy?.()),
    onFiles: addFiles,
    onRejectedEmpty: () => {
      showDropError();
    },
  });

  return {
    getFiles: () => [...selected],
    clear: () => {
      selected = [];
      clearDropError();
      syncInputFiles();
      renderList();
    },
    dispose: () => {
      unbindDrop();
    },
  };
}
